import { useState, useEffect, useCallback } from 'react';
import { fetchLabels, deleteLabel } from '../../../api/labels';

/**
 * useLabelList - 标签列表管理 Hook
 * 负责获取、删除和管理用户创建的标签列表
 */
export function useLabelList() {
  const [labels, setLabels] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    pageNum: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  });

  // 获取标签列表
  const loadLabels = useCallback(async (pageNum = 1, pageSize = 10) => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await fetchLabels({ pageNum, pageSize });
      
      // 处理不同的响应格式
      let labelList = [];
      let paginationData = {};

      if (Array.isArray(result)) {
        // 如果直接返回数组
        labelList = result;
      } else if (result?.list || result?.data) {
        // 如果返回分页数据 { list: [], total: 100 }
        labelList = result.list || result.data || [];
        paginationData = {
          total: result.total || labelList.length,
          totalPages: result.totalPages || Math.ceil((result.total || labelList.length) / pageSize),
        };
      } else if (result?.records) {
        // 如果返回 { records: [], total: 100 }
        labelList = result.records || [];
        paginationData = {
          total: result.total || labelList.length,
          totalPages: result.totalPages || Math.ceil((result.total || labelList.length) / pageSize),
        };
      }

      setLabels(labelList);
      setPagination(prev => ({
        ...prev,
        pageNum,
        pageSize,
        ...paginationData,
      }));
    } catch (err) {
      console.error('Failed to load labels:', err);
      setError(err.message || '加载标签列表失败');
      setLabels([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 删除标签
  const handleDelete = useCallback(async (labelId) => {
    if (!window.confirm('确定要删除这个标签吗？删除后无法恢复。')) {
      return false;
    }

    try {
      setIsLoading(true);
      await deleteLabel(labelId);
      // 删除成功后重新加载列表
      await loadLabels(pagination.pageNum, pagination.pageSize);
      return true;
    } catch (err) {
      console.error('Failed to delete label:', err);
      setError(err.message || '删除标签失败');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [loadLabels, pagination.pageNum, pagination.pageSize]);

  // 初始化加载
  useEffect(() => {
    loadLabels(1, 10);
  }, [loadLabels]);

  // 切换页码
  const changePage = useCallback((newPageNum) => {
    loadLabels(newPageNum, pagination.pageSize);
  }, [loadLabels, pagination.pageSize]);

  // 刷新列表
  const refresh = useCallback(() => {
    loadLabels(pagination.pageNum, pagination.pageSize);
  }, [loadLabels, pagination.pageNum, pagination.pageSize]);

  return {
    labels,
    isLoading,
    error,
    pagination,
    loadLabels,
    handleDelete,
    changePage,
    refresh,
  };
}

